import React, {useEffect, useRef, useState} from 'react';
import useViewportBottomInset from './useViewportBottomInset';

/** How long a user sits without scrolling before we suggest there is more. */
const NUDGE_DELAY_MS = 5000;

/**
 * Movement that counts as the user scrolling, rather than the browser doing it to itself.
 * Mobile browsers emit scroll at 0-20px from the address bar collapsing, viewport resizes
 * and scroll restoration, none of which mean the user has engaged. Dismissing on any scroll
 * event meant the nudge died before it could appear on a real phone while passing every
 * headless test — headless Chrome fires none of those.
 */
const SCROLL_DISMISS_PX = 24;

export type ScrollNudgeProps = {
  /** Fired once, when the chevron first becomes visible. */
  onShown: () => void;
};

/**
 * A late, quiet chevron in the bottom-right corner, hinting that the page continues.
 *
 * Content below the fold is worth nothing to a user who never learns it is there, and this
 * paywall gives no reason to scroll: the CTA is the natural end of the screen. After
 * {@link NUDGE_DELAY_MS} of no scrolling, the chevron fades in. A real scroll retires it.
 *
 * **It floats over whatever is beneath it, and is deliberately inert.** Earlier versions
 * refused to show unless the corner was free, which measured out badly: a free 48px square
 * only exists above a ~700px viewport, and an iPhone 15 in Safari with the toolbars up is
 * ~658px — the CTA ends 22px from the bottom and the paywall's own content already
 * overflows. Guarding the placement therefore hid the chevron on exactly the devices that
 * needed it. Overlapping is the simpler and more reliable answer.
 *
 * What overlapping must not cost is a tap on the purchase button, so this carries
 * `pointer-events: none`: it is drawn over the CTA and can never intercept a touch meant
 * for it. That makes the chevron a signal rather than a control — it cannot be tapped to
 * scroll, which is a small loss next to swallowing taps on the one element that matters.
 */
const ScrollNudge = ({onShown}: ScrollNudgeProps) => {
  const [visible, setVisible] = useState(false);
  const scrolledRef = useRef(false);
  const shownReportedRef = useRef(false);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  // Defensive: 0 unless the browser's own chrome covers the bottom of the layout viewport.
  const bottomInset = useViewportBottomInset();

  useEffect(() => {
    const onScroll = () => {
      if ((window.scrollY || document.documentElement.scrollTop) < SCROLL_DISMISS_PX) return;
      scrolledRef.current = true;
      setVisible(false);
      window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, {passive: true});

    const timer = window.setTimeout(() => {
      if (scrolledRef.current) return;
      // Nothing below to point at — don't hint at a page that cannot move.
      if (document.documentElement.scrollHeight - window.innerHeight < 1) return;
      setVisible(true);
      if (!shownReportedRef.current) {
        shownReportedRef.current = true;
        onShownRef.current();
      }
    }, NUDGE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div
      className={visible ? 'i36-scroll-nudge i36-scroll-nudge-visible' : 'i36-scroll-nudge'}
      style={{'--i36-bottom-inset': `${bottomInset}px`} as React.CSSProperties}
      aria-hidden={true}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

export default ScrollNudge;
